import api from '../api/axiosInstance';
import {
  NiiRunType,
  NiiRunStatus,
  SummaryCardResponse,
  ReportListResponse,
  ReportSummary,
  ReportStatusPollResponse,
  CpiRelease,
  CreateRunsRequest,
  CreateRunsResponse,
  RunListResponse,
  RunStatusResponse,
  UploadResponse,
  GplScriptBundle,
  GplReportContentResponse
} from '../components/NII/types/nii.types';

export const niiService = {
  // --- Landing page ---

  /** GET /api/nii/runs?type=X&groupBy=Y — summary cards for landing page */
  getReleaseSummaryCards: (type: NiiRunType, groupBy?: string): Promise<SummaryCardResponse> =>
    api.get<SummaryCardResponse>('/api/nii/runs', {
      params: { type, groupBy: groupBy || 'release' }
    }).then((res) => res.data),

  /** GET /api/nii/reports?type=X&releaseName=Y&status=complete — completed reports */
  getReports: (params: {
    type?: NiiRunType;
    releaseName?: string;
    gplVersion?: string;
    status?: NiiRunStatus;
    page?: number;
    pageSize?: number;
  }): Promise<ReportListResponse> =>
    api.get<ReportListResponse>('/api/nii/reports', { params }).then((res) => res.data),

  // --- Generation form ---

  /** GET /api/nii/releases?startDate=X&endDate=Y — CPI releases for dropdown */
  getCpiReleases: (startDate?: string, endDate?: string): Promise<CpiRelease[]> =>
    api.get<CpiRelease[]>('/api/nii/releases', {
      params: { startDate, endDate }
    }).then((res) => res.data),

  /** POST /api/nii/runs — trigger run(s) */
  createRuns: (body: CreateRunsRequest): Promise<CreateRunsResponse> =>
    api.post<CreateRunsResponse>('/api/nii/runs', body).then((res) => res.data),

  /** POST /api/nii/uploads?purpose=network_inventory — upload ENM data file */
  uploadFile: (file: File, purpose: string): Promise<UploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return api
      .post<UploadResponse>(`/api/nii/uploads?purpose=${encodeURIComponent(purpose)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => res.data);
  },

  // --- Report content & status ---

  /** GET /api/nii/reports/:reportId/status — poll single report status */
  pollReportStatus: (reportId: string): Promise<ReportStatusPollResponse> =>
    api.get<ReportStatusPollResponse>(`/api/nii/reports/${reportId}/status`).then((res) => res.data),

  /** GET /api/nii/reports/:reportId?query — paginated report content */
  getReportContent: (reportId: string, query: string) =>
    api.get(`/api/nii/reports/${reportId}?${query}`),

  /** GET /api/nii/reports/:reportId/export?query — download as Excel */
  exportReport: (reportId: string, query: string) =>
    api.get(`/api/nii/reports/${reportId}/export?${query}`, { responseType: 'blob' }),

  // --- Execution table ---

  /** GET /api/nii/runs?type=X&page=1&pageSize=15 — paginated run list */
  listRuns: (params: {
    type?: NiiRunType;
    page?: number;
    pageSize?: number;
  }): Promise<RunListResponse> =>
    api.get<RunListResponse>('/api/nii/runs', { params }).then((res) => res.data),

  /** GET /api/nii/runs/:runId — single run details */
  getRun: (runId: string): Promise<RunStatusResponse> =>
    api.get<RunStatusResponse>(`/api/nii/runs/${runId}`).then((res) => res.data),

  /** GET /api/nii/runs/:runId/reports — reports produced by a run */
  getRunReports: (runId: string): Promise<ReportSummary[]> =>
    api.get<ReportSummary[]>(`/api/nii/runs/${runId}/reports`).then((res) => res.data),

  // --- GPL Script Management ---

  /** GET /api/nii/uploads?purpose=gpl_scripts — list all GPL script bundles */
  listGplScripts: (): Promise<GplScriptBundle[]> =>
    api.get<GplScriptBundle[]>('/api/nii/uploads', {
      params: { purpose: 'gpl_scripts' }
    }).then((res) => res.data),

  /** POST /api/nii/uploads?purpose=gpl_scripts&gplVersion=X — upload GPL script zip */
  uploadGplScripts: (file: File, gplVersion: string): Promise<GplScriptBundle> => {
    const formData = new FormData();
    formData.append('file', file);
    return api
      .post<GplScriptBundle>(
        `/api/nii/uploads?purpose=gpl_scripts&gplVersion=${encodeURIComponent(gplVersion)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )
      .then((res) => res.data);
  },

  /** GET /api/nii/uploads/:uploadId/files/:scriptName — get raw script text */
  getScriptContent: (uploadId: string, scriptName: string): Promise<string> =>
    api.get<string>(`/api/nii/uploads/${uploadId}/files/${encodeURIComponent(scriptName)}`, {
      responseType: 'text',
      transformResponse: [(data: string) => data]
    }).then((res) => res.data),

  // --- GPL Audit Reports ---

  /** GET /api/nii/reports/:reportId?query — GPL audit report content (typed) */
  getGplReportContent: (reportId: string, query: string): Promise<GplReportContentResponse> =>
    api.get<GplReportContentResponse>(`/api/nii/reports/${reportId}?${query}`).then((res) => res.data),

  /** GET /api/nii/reports?type=gpl_audit&nodeName=X&gplVersion=Y&status=complete — audit history for a node */
  getNodeAuditHistory: (nodeName: string, gplVersion: string): Promise<ReportSummary[]> =>
    api.get<ReportListResponse>('/api/nii/reports', {
      params: { type: 'gpl_audit', nodeName, gplVersion, status: 'complete' }
    }).then((res) => res.data.data),

  /** POST /api/nii/uploads?purpose=node_dump — upload node dump for GPL audit */
  uploadNodeDump: (file: File): Promise<UploadResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    return api
      .post<UploadResponse>('/api/nii/uploads?purpose=node_dump', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      .then((res) => res.data);
  }
};
